import {
    Box,
    Button,
    Grid,
    FormControl,
    FormLabel,
    Input,
    VStack,
    HStack,
    useToast,
    Heading,
    Card,
    CardHeader,
    CardBody,
    Divider,
    Textarea,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Spacer,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    useDisclosure,
    Text,
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import {
    usePastPerformances,
    useCreatePastPerformance,
    useUpdatePastPerformance,
    useDeletePastPerformance,
} from '../../services/contractor-profile.hooks';

export function PastPerformanceTab() {
    const toast = useToast();
    const { data: pastPerformances, isLoading } = usePastPerformances();
    const createItem = useCreatePastPerformance();
    const updateItem = useUpdatePastPerformance();
    const deleteItem = useDeletePastPerformance();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editingId, setEditingId] = useState<number | null>(null);

    const [formData, setFormData] = useState({
        project_name: '',
        agency: '',
        contract_value: 0,
        start_date: '',
        end_date: '',
        description: '',
        reference_name: '',
        reference_contact: '',
    });

    const handleOpenModal = (item?: any) => {
        if (item) {
            setEditingId(item.id);
            setFormData({
                project_name: item.project_name || '',
                agency: item.agency || '',
                contract_value: item.contract_value || 0,
                start_date: item.start_date ? new Date(item.start_date).toISOString().split('T')[0] : '',
                end_date: item.end_date ? new Date(item.end_date).toISOString().split('T')[0] : '',
                description: item.description || '',
                reference_name: item.reference_name || '',
                reference_contact: item.reference_contact || '',
            });
        } else {
            setEditingId(null);
            setFormData({
                project_name: '',
                agency: '',
                contract_value: 0,
                start_date: '',
                end_date: '',
                description: '',
                reference_name: '',
                reference_contact: '',
            });
        }
        onOpen();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'number' ? Number(value) : value,
        }));
    };

    const handleSave = () => {
        // Prepare data for API
        const payload = {
            ...formData,
            start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
            end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
        };

        if (editingId) {
            updateItem.mutate(
                { id: editingId, data: payload },
                {
                    onSuccess: () => {
                        toast({ title: 'Past performance updated', status: 'success', duration: 2000 });
                        onClose();
                    },
                }
            );
        } else {
            createItem.mutate(payload, {
                onSuccess: () => {
                    toast({ title: 'Past performance added', status: 'success', duration: 2000 });
                    onClose();
                },
            });
        }
    };

    const handleDelete = (id: number) => {
        if (confirm('Are you sure you want to delete this past performance record?')) {
            deleteItem.mutate(id, {
                onSuccess: () => {
                    toast({ title: 'Record deleted', status: 'info', duration: 2000 });
                },
            });
        }
    };

    if (isLoading) return <Box>Loading...</Box>;

    return (
        <Card>
            <CardHeader>
                <HStack>
                    <Heading size="md">Past Performance & Project References</Heading>
                    <Spacer />
                    <Button leftIcon={<MdAdd />} colorScheme="brand" size="sm" onClick={() => handleOpenModal()}>
                        Add Project
                    </Button>
                </HStack>
            </CardHeader>
            <Divider color="gray.200" />
            <CardBody>
                {pastPerformances?.length === 0 ? (
                    <Text color="gray.500">No past performances recorded. Add your previous projects to build credibility.</Text>
                ) : (
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th>Project Name</Th>
                                <Th>Client/Agency</Th>
                                <Th isNumeric>Value ($)</Th>
                                <Th>Timeframe</Th>
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {pastPerformances?.map((item: any) => (
                                <Tr key={item.id}>
                                    <Td fontWeight="medium">{item.project_name}</Td>
                                    <Td>{item.agency}</Td>
                                    <Td isNumeric>
                                        {item.contract_value ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(item.contract_value) : '-'}
                                    </Td>
                                    <Td>
                                        {item.start_date ? new Date(item.start_date).getFullYear() : '—'} - {item.end_date ? new Date(item.end_date).getFullYear() : 'Present'}
                                    </Td>
                                    <Td>
                                        <HStack spacing={2}>
                                            <IconButton
                                                aria-label="Edit"
                                                icon={<MdEdit />}
                                                size="xs"
                                                variant="ghost"
                                                onClick={() => handleOpenModal(item)}
                                            />
                                            <IconButton
                                                aria-label="Delete"
                                                icon={<MdDelete />}
                                                size="xs"
                                                colorScheme="red"
                                                variant="ghost"
                                                onClick={() => handleDelete(item.id)}
                                            />
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                )}
            </CardBody>

            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{editingId ? 'Edit Project' : 'Add Project'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4}>
                            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4} w="full">
                                <FormControl isRequired gridColumn="1 / -1">
                                    <FormLabel>Project Name</FormLabel>
                                    <Input name="project_name" value={formData.project_name} onChange={handleChange} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Client / Agency</FormLabel>
                                    <Input name="agency" value={formData.agency} onChange={handleChange} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Contract Value ($)</FormLabel>
                                    <Input name="contract_value" type="number" value={formData.contract_value} onChange={handleChange} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Start Date</FormLabel>
                                    <Input name="start_date" type="date" value={formData.start_date} onChange={handleChange} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>End Date</FormLabel>
                                    <Input name="end_date" type="date" value={formData.end_date} onChange={handleChange} />
                                </FormControl>
                                <FormControl gridColumn="1 / -1">
                                    <FormLabel>Description / Scope of Work</FormLabel>
                                    <Textarea name="description" value={formData.description} onChange={handleChange} rows={3} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Reference Name</FormLabel>
                                    <Input name="reference_name" value={formData.reference_name} onChange={handleChange} />
                                </FormControl>
                                <FormControl>
                                    <FormLabel>Reference Contact Info</FormLabel>
                                    <Input name="reference_contact" value={formData.reference_contact} onChange={handleChange} placeholder="Email or Phone" />
                                </FormControl>
                            </Grid>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="brand"
                            onClick={handleSave}
                            isLoading={createItem.isPending || updateItem.isPending}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Card>
    );
}
